/**
 * Utilities for extracting trigger node filtering context.
 *
 * Mirrors backend logic in `backend/logiblocks/services/node_defaults/path_node.py`.
 * Used to hide redundant fields in LHS conditional variable pickers.
 */

import type { ReactFlowNode } from '../types';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';

export interface TriggerFilterContext {
  /** The trigger node type (e.g., "alert_trigger", "location_trigger") */
  triggerType: string;

  // Alert Trigger fields
  alertTypes?: string[];
  isAllAlertTypes?: boolean;
  alertCoveredPhases?: Set<string>;

  // Location Trigger fields
  locationId?: string;

  // ETA Change Trigger fields
  etaChangeBy?: string;

  // Status Progress Trigger fields
  statusProgressValues?: string[];

  // Inbound Trigger fields (new)
  inboundEventType?: string;

  // Manual Execution Trigger fields
  externalTrigger?: string;
}

// Maps alert types to their phases - mirrors backend ALERT_TYPE_TO_PHASE
const ALERT_TYPE_TO_PHASE: Record<string, string> = {
  low_battery_at_origin: 'PRE_PICK',
  driver_late_to_pickup: 'PRE_PICK',
  flight_delayed: 'PRE_FLIGHT',
  flight_delayed_beyond_threshold: 'PRE_FLIGHT',
  shipment_not_loaded_onto_flight: 'PRE_FLIGHT',
  sensor_location_still_at_pickup: 'PRE_FLIGHT',
  offload_delay_after_flight_arrival: 'POST_FLIGHT',
  sensor_location_not_at_drop: 'POST_FLIGHT',
  driver_late_to_recover_shipment: 'POST_FLIGHT',
  driver_cant_find_location: 'AT_DELIVERY',
  delivered_to_wrong_address: 'AT_DELIVERY',
  delivered_to_wrong_recipient: 'AT_DELIVERY',
  proof_of_delivery_missing: 'AT_DELIVERY',
  weather_event_impact_expected: 'CONTINUOUS',
  geopolitical_event_impact_expected: 'CONTINUOUS',
  status_update_overdue: 'CONTINUOUS',
  flight_landing_close_to_cargo_closing_time: 'CONTINUOUS',
  viability_window_risk_expiring: 'CONTINUOUS',
  viability_at_risk_before_arrival: 'CONTINUOUS',
  viability_window_nearly_exhausted: 'CONTINUOUS',
  viability_window_exhausted: 'CONTINUOUS',
  low_battery: 'CONTINUOUS',
  vehicle_idle_in_transit: 'CONTINUOUS',
  temperature_nearing_limit: 'CONTINUOUS',
  temperature_excursion_detected: 'CONTINUOUS',
  temperature_excursion_pre_alarm: 'CONTINUOUS',
  excessive_light_exposure_detected: 'CONTINUOUS',
  excessive_tilt_detected: 'CONTINUOUS',
  excessive_pressure_detected: 'CONTINUOUS',
  excessive_humidity_detected: 'CONTINUOUS',
  excessive_shock_detected: 'CONTINUOUS',
  route_deviation_detected: 'CONTINUOUS',
  delivery_overdue: 'AT_DELIVERY',
  vessel_delayed_or_cancelled: 'OTHER',
  transport_provider_exception: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  customer_not_available_or_business_closed: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  incorrect_address: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  rescheduled_by_recipient: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  rescheduled_by_transport_provider: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  delivery_restrictions: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  refused_by_recipient: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  weather_impact: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  customs_issue: 'TRANSPORT_PROVIDER_EXCEPTIONS',
  other: 'TRANSPORT_PROVIDER_EXCEPTIONS',
};

// Mirrors backend STATUS_PROGRESS_FLAG_TO_VALUE
// Maps StatusProgressTriggerData boolean fields to their canonical status values
const STATUS_PROGRESS_FLAG_TO_VALUE: Array<[string, string]> = [
  ['is_created', 'pending'], // Created = Pending status
  ['is_pickup_confirmation', 'awaiting_pickup'], // Pickup = Awaiting Pickup
  ['is_incomplete', 'incomplete'], // Incomplete
  ['is_out_for_delivery', 'out_for_delivery'], // Out for Delivery
  ['is_delivery_exception', 'delivery_exception'], // Delivery Exception
  ['is_delivery_confirmation', 'delivered'], // Delivered
];

/**
 * Extracts filtering context from a trigger node.
 * Returns null if the node is not a recognized trigger type.
 *
 * @param triggerNode - The trigger node from the workflow graph
 * @returns TriggerFilterContext or null
 */
export function getTriggerFilterContext(
  triggerNode: ReactFlowNode | undefined
): TriggerFilterContext | null {
  if (!triggerNode) {
    return null;
  }

  const nodeData = triggerNode.data as LBNodeData;
  const triggerType = nodeData.alert_trigger
    ? 'alert_trigger'
    : nodeData.location_trigger
      ? 'location_trigger'
      : nodeData.eta_change_trigger
        ? 'eta_change_trigger'
        : nodeData.status_progress_trigger
          ? 'status_progress_trigger'
          : nodeData.inbound_event_trigger
            ? 'inbound_event_trigger'
            : nodeData.inbound_trigger
              ? 'inbound_trigger'
              : nodeData.manual_execution_trigger
                ? 'manual_execution_trigger'
                : nodeData.schedule_trigger_v2
                  ? 'schedule_trigger_v2'
                  : nodeData.inbound_call_trigger
                    ? 'inbound_call_trigger'
                    : null;

  if (!triggerType) {
    return null;
  }

  const ctx: TriggerFilterContext = { triggerType };

  switch (triggerType) {
    case 'alert_trigger': {
      const alertTrigger = nodeData.alert_trigger!;
      const alertTypes = alertTrigger.alert_types || [];
      const isAllAlertTypes =
        alertTrigger.select_all_alert_types || alertTypes.length === 0;

      const coveredPhases = new Set<string>();
      for (const alertType of alertTypes) {
        const phase = ALERT_TYPE_TO_PHASE[alertType] || 'CONTINUOUS';
        coveredPhases.add(phase);
      }

      ctx.alertTypes = alertTypes;
      ctx.isAllAlertTypes = isAllAlertTypes;
      ctx.alertCoveredPhases = coveredPhases;
      break;
    }

    case 'location_trigger': {
      const locationTrigger = nodeData.location_trigger!;
      ctx.locationId = locationTrigger.location || '';
      break;
    }

    case 'eta_change_trigger': {
      const etaTrigger = nodeData.eta_change_trigger!;
      ctx.etaChangeBy = etaTrigger.change_by || '';
      break;
    }

    case 'status_progress_trigger': {
      const statusTrigger = nodeData.status_progress_trigger!;
      const selectedValues: string[] = [];
      const flagValues = new Map<string, unknown>(
        Object.entries(statusTrigger)
      );

      for (const [flag, value] of STATUS_PROGRESS_FLAG_TO_VALUE) {
        if (flagValues.get(flag) === true) {
          selectedValues.push(value);
        }
      }

      ctx.statusProgressValues = selectedValues;
      break;
    }

    case 'inbound_trigger': {
      const inboundTrigger = nodeData.inbound_trigger!;
      ctx.inboundEventType = inboundTrigger.inbound_event_type || '';
      break;
    }

    case 'manual_execution_trigger': {
      const manualTrigger = nodeData.manual_execution_trigger!;
      ctx.externalTrigger = manualTrigger.external_trigger || '';
      break;
    }

    case 'inbound_event_trigger':
    case 'inbound_call_trigger':
    case 'schedule_trigger_v2':
      // These trigger types don't have filtering logic
      break;
  }

  return ctx;
}

/**
 * Returns true if the trigger has any active filters that might hide fields.
 */
export function hasActiveFilters(ctx: TriggerFilterContext | null): boolean {
  if (!ctx) return false;

  return !!(
    (ctx.alertTypes && ctx.alertTypes.length > 0 && !ctx.isAllAlertTypes) ||
    ctx.locationId ||
    ctx.etaChangeBy ||
    (ctx.statusProgressValues && ctx.statusProgressValues.length > 0) ||
    ctx.inboundEventType ||
    ctx.externalTrigger
  );
}
